import { io, type Socket } from "socket.io-client";
import { env } from "@astro/config";

export type SocketHandler = (...args: unknown[]) => void;
export type ConnectionListener = (connected: boolean) => void;
export type Unsubscribe = () => void;

export interface SocketManagerOptions {
  /**
   * Socket server origin. Defaults to VITE_SOCKET_URL when set, otherwise the
   * origin of VITE_API_BASE_URL (path stripped — socket.io mounts on its own
   * `path`, not under /api).
   */
  url?: string;
  /** socket.io mount path on the server. */
  path?: string;
  /**
   * Called on an auth-flavoured connect_error. Return true when the session
   * was refreshed and a reconnect should be attempted. Tried once per
   * connection cycle to avoid a refresh loop.
   */
  onAuthError?: () => Promise<boolean>;
}

function defaultSocketUrl(): string {
  if (env.VITE_SOCKET_URL) return env.VITE_SOCKET_URL;
  return new URL(env.VITE_API_BASE_URL).origin;
}

function isAuthError(message: string): boolean {
  const msg = message.toLowerCase();
  return msg.includes("token") || msg.includes("auth") || msg.includes("unauthorized") || msg.includes("invalid");
}

/**
 * Framework-free socket.io wrapper shared by every app.
 *
 * Auth model: the handshake carries the httpOnly parent-domain session cookie
 * (withCredentials) — the same cookie the auth BFF seals on login. The browser
 * never handles a token, so there is nothing to put in `auth: {}`. The socket
 * server must live on a *.astrorajumaharaj.com origin and allow credentialed
 * CORS from the app origins.
 *
 * Handlers are kept in a registry independent of the live socket instance, so
 * subscriptions made while disconnected (or before login) attach automatically
 * once a connection exists, and survive logout/login socket swaps.
 */
export class SocketManager {
  private socket: Socket | null = null;
  private handlers = new Map<string, Set<SocketHandler>>();
  private connectionListeners = new Set<ConnectionListener>();
  private authRetried = false;
  private options: SocketManagerOptions;

  constructor(options: SocketManagerOptions = {}) {
    this.options = options;
  }

  get connected(): boolean {
    return this.socket?.connected ?? false;
  }

  getSocket(): Socket | null {
    return this.socket;
  }

  /** Open a connection (no-op if one already exists). */
  connect(): void {
    if (this.socket) {
      if (!this.socket.connected) this.socket.connect();
      return;
    }

    const socket = io(this.options.url ?? defaultSocketUrl(), {
      path: this.options.path,
      withCredentials: true,
      transports: ["websocket", "polling"],
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1500,
      reconnectionDelayMax: 10000,
      randomizationFactor: 0.5,
      reconnectionAttempts: Infinity,
      timeout: 20000,
    });

    socket.on("connect", () => {
      this.authRetried = false;
      this.notifyConnection(true);
    });

    socket.on("disconnect", (reason) => {
      this.notifyConnection(false);
      // The server kicked us (e.g. session expired server-side) — socket.io
      // does not auto-reconnect on this reason, so try one refresh cycle.
      if (reason === "io server disconnect") void this.tryAuthRecovery();
    });

    socket.on("connect_error", (err) => {
      this.notifyConnection(false);
      if (isAuthError(String(err?.message ?? ""))) void this.tryAuthRecovery();
    });

    // Attach everything registered so far. socket.io keeps listeners across
    // reconnects of the same instance, so this runs once per instance — not
    // per connect — which avoids duplicate deliveries.
    this.handlers.forEach((set, event) => {
      set.forEach((cb) => socket.on(event, cb));
    });

    this.socket = socket;
  }

  /** Tear down the connection; the handler registry is kept for the next connect(). */
  disconnect(): void {
    const socket = this.socket;
    if (!socket) return;
    this.socket = null;
    socket.removeAllListeners();
    socket.disconnect();
    this.notifyConnection(false);
  }

  /** Subscribe to a server event. Returns an unsubscribe function. */
  on(event: string, cb: SocketHandler): Unsubscribe {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    if (!set.has(cb)) {
      set.add(cb);
      this.socket?.on(event, cb);
    }
    return () => this.off(event, cb);
  }

  off(event: string, cb: SocketHandler): void {
    const set = this.handlers.get(event);
    if (set) {
      set.delete(cb);
      if (set.size === 0) this.handlers.delete(event);
    }
    this.socket?.off(event, cb);
  }

  /** Emit to the server. Returns false (and drops the event) while disconnected. */
  emit(event: string, payload?: unknown): boolean {
    if (!this.socket?.connected) {
      console.warn("[@astro/socket] emit while disconnected:", event);
      return false;
    }
    this.socket.emit(event, payload);
    return true;
  }

  /** Observe connection state. Returns an unsubscribe function. */
  onConnectionChange(cb: ConnectionListener): Unsubscribe {
    this.connectionListeners.add(cb);
    return () => this.connectionListeners.delete(cb);
  }

  private notifyConnection(connected: boolean): void {
    this.connectionListeners.forEach((cb) => cb(connected));
  }

  private async tryAuthRecovery(): Promise<void> {
    if (this.authRetried || !this.options.onAuthError) return;
    this.authRetried = true;
    try {
      const refreshed = await this.options.onAuthError();
      if (refreshed) this.socket?.connect();
    } catch (err) {
      console.warn("[@astro/socket] session refresh failed:", err);
    }
  }
}

export function createSocketManager(options?: SocketManagerOptions): SocketManager {
  return new SocketManager(options);
}
